import React, { useEffect } from 'react';
import { gql, useMutation } from '@apollo/client';

import { useLogger } from '~Hooks';
import { RefundProps } from '../types';
import { KlarnaRefund, KlarnaRefundVariables } from './__queries__';

/**
 * Same backend refund endpoint the card payments use, plus the returned line
 * items. The payment behind a Klarna POS sale is an ordinary Adyen ecommerce
 * payment, so the refund itself is a standard Checkout API refund against its
 * pspReference.
 */
const KLARNA_REFUND_REQUEST = gql`
  mutation KlarnaRefund(
    $transactionId: String
    $amount: Int
    $lineItems: [AdyenRefundLineItemInput!]
  ) {
    posAdyenRefundRequest(transactionId: $transactionId, amount: $amount, lineItems: $lineItems) {
      success
      message
      method
      cardIdentifier
    }
  }
`;

/**
 * Refunds a Klarna payment.
 *
 * Klarna is taken through Adyen Pay by Link, never through a terminal, so there
 * is no terminal-side refund path the way there is for cards - it always goes
 * through the backend.
 *
 * The one thing that differs from a card refund is that Adyen requires the
 * returned items on a PARTIAL refund of an open invoice payment: Klarna reissues
 * the shopper's invoice from them, and rejects the refund if they are missing or
 * do not add up to the refunded amount. A full refund needs none.
 */
const RefundKlarna = (props: RefundProps) => {
  const { amount, onCompleted, onFail, transactionId, items, isFullRefund } = props;

  const [klarnaRefundRequest] = useMutation<KlarnaRefund, KlarnaRefundVariables>(
    KLARNA_REFUND_REQUEST,
  );
  const logger = useLogger('refund-klarna');

  useEffect(() => {
    if (amount === 0) {
      logger.info('Refund - Amount is 0, no need to refund');

      onCompleted();
      return;
    }

    /**
     * Line items are built the same way the payment link was: one line per
     * refunded row with quantity 1 and the row total as the price, rather than
     * a derived unit price. Adyen totals a line as (price x quantity), so
     * dividing a row total by its count would round in minor units and the
     * lines would no longer add up to the refunded amount - which Klarna
     * rejects outright.
     */
    const lineItems =
      isFullRefund || !items?.length
        ? undefined
        : items.map((item) => ({
            id: item.id,
            description: item.title,
            quantity: 1,
            amountIncludingTax: item.amount - item.discount,
            // Adyen wants basis points: 25% VAT is 2500.
            taxPercentage: Math.round(item.vatRate * 100),
          }));

    if (lineItems) {
      const lineItemTotal = lineItems.reduce((sum, line) => sum + line.amountIncludingTax, 0);

      // Worth knowing about before Adyen tells us: if these disagree, Klarna
      // declines the refund and the reason is not obvious from its response.
      if (lineItemTotal !== amount) {
        logger.warn('Refund - Klarna line items do not add up to the refunded amount', {
          lineItemTotal,
          amount,
          transactionId,
        });
      }
    }

    logger.info('Refund - Klarna refund started', {
      transactionId,
      amount,
      isFullRefund,
      lineItemCount: lineItems?.length ?? 0,
    });

    klarnaRefundRequest({
      variables: {
        transactionId,
        amount,
        lineItems,
      },
      fetchPolicy: 'no-cache',
    })
      .then(({ data }) => {
        if (!data?.posAdyenRefundRequest.success) {
          throw new Error(data?.posAdyenRefundRequest.message ?? 'Unknown error');
        }

        logger.info('Refund - Klarna refund successful on TransactionId: ' + transactionId);
        onCompleted();
      })
      .catch((e) => {
        logger.error('Refund - Klarna refund failed on TransactionId: ' + transactionId, {
          errorMessage: e.message,
          amount,
          isFullRefund,
        });
        onFail(e.message);
      });
  }, []);

  return <></>;
};

export default RefundKlarna;
